import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Shield, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const heroVideos = ['/technogym-1.mp4', '/technogym-2.mp4', '/technogym-3.mp4', '/technogym-4.mp4', '/technogym-5.mp4'];

export const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);

  useEffect(() => {
    const video = videoRefs.current[activeIndex];
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
    const next = videoRefs.current[(activeIndex + 1) % heroVideos.length];
    if (next && next.preload === 'none') next.preload = 'auto';
    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % heroVideos.length);
    }, 10000);
    return () => clearTimeout(timer);
  }, [activeIndex]);

  return (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Video slideshow background */}
      <div className="absolute inset-0 bg-black">
        {heroVideos.map((src, idx) => (
          <video
            key={src}
            ref={(el) => { videoRefs.current[idx] = el; }}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
              idx === activeIndex ? 'opacity-100' : 'opacity-0'
            }`}
            muted
            loop
            playsInline
            preload={idx === 0 ? 'auto' : 'none'}
            autoPlay={idx === 0}
            poster={idx === 0 ? '/hero-poster.webp' : undefined}
            data-testid={`hero-video-${idx + 1}`}
          >
            <source src={src} type="video/mp4" />
          </video>
        ))}
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40" />
      </div>

      <div className="container mx-auto px-4 py-24 lg:py-32 relative z-10">
        <div className="max-w-2xl">
          <div className="space-y-8">
            {/* Heading */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight animate-slide-up stagger-1 break-words">
              Installatie &amp;
              <span className="block text-yellow-400">transport</span>
              <span className="block text-white/80">fitnessapparatuur</span>
            </h1>

            {/* Description */}
            <p className="text-xl text-white/85 leading-relaxed max-w-xl animate-slide-up stagger-2 drop-shadow-md">
              {companyInfo.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-3">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-7 text-lg rounded-2xl shadow-xl shadow-yellow-400/30 hover:shadow-yellow-400/40 hover:scale-[1.02] transition-all duration-300 group font-bold tracking-wide" data-testid="hero-call-button">
                  <Phone className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  Bel: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/offerte">
                <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:border-yellow-400 hover:bg-yellow-400 hover:text-black px-8 py-7 text-lg rounded-2xl hover:scale-[1.02] transition-all duration-300 font-bold tracking-wide" data-testid="hero-offerte-button">
                  Offerte aanvragen
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-8 pt-6 animate-slide-up stagger-4">
              {[
                { icon: Shield, text: 'Vakkundige montage' },
                { icon: Clock, text: 'Snel inzetbaar' },
                { icon: CheckCircle2, text: 'Alle merken' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-white/80 group cursor-default">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-yellow-400/20 group-hover:scale-110 transition-all duration-300">
                    <item.icon className="w-5 h-5 text-yellow-400" />
                  </div>
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-8 z-10 flex gap-2" data-testid="hero-slide-indicators">
        {heroVideos.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveIndex(idx)}
            aria-label={`Video ${idx + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              idx === activeIndex ? 'w-8 bg-yellow-400' : 'w-3 bg-white/30 hover:bg-white/60'
            }`}
            data-testid={`hero-slide-dot-${idx + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-yellow-400 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};
